'use client'

import { useState, useRef } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import Editor from '@monaco-editor/react'

export default function NewScriptPage() {
  const router = useRouter()
  const [itemType, setItemType] = useState<'SNIPPET' | 'PROJECT'>('SNIPPET')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [language, setLanguage] = useState('typescript')
  
  // Snippet State
  const [code, setCode] = useState('')
  
  // Project State
  const [files, setFiles] = useState([{ id: '1', path: 'src/index.ts', content: '// Entry point\n', language: 'typescript' }])
  const [activeFileId, setActiveFileId] = useState('1')
  
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null)
  const [isDraggingGlobal, setIsDraggingGlobal] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)

  const activeFile = files.find(f => f.id === activeFileId)

  // --- IDE SORTING LOGIC (Folders First, then Files, Alphabetical) ---
  const sortedFiles = [...files].sort((a, b) => {
    const aParts = a.path.split('/')
    const bParts = b.path.split('/')
    
    for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
      if (aParts[i] !== bParts[i]) {
        // A part is a folder if it's not the last part OR if it's explicitly a .keep file
        const aIsFolder = i < aParts.length - 1 || aParts[i] === '.keep'
        const bIsFolder = i < bParts.length - 1 || bParts[i] === '.keep'
        
        if (aIsFolder && !bIsFolder) return -1
        if (!aIsFolder && bIsFolder) return 1
        return aParts[i].localeCompare(bParts[i])
      }
    }
    return aParts.length - bParts.length
  })

  // --- VIRTUAL FILE SYSTEM ACTIONS ---

  const handleAddFile = () => {
    // Inherit the directory of the currently active file
    const activePath = activeFile?.path || ''
    const parts = activePath.split('/')
    parts.pop() // Remove the filename
    const folderPrefix = parts.length > 0 ? parts.join('/') + '/' : ''
    
    const newId = Date.now().toString()
    setFiles([...files, { id: newId, path: `${folderPrefix}new_file_${files.length + 1}.ts`, content: '', language: 'typescript' }])
    setActiveFileId(newId)
  }

  const handleAddFolder = () => {
    const activePath = activeFile?.path || ''
    const parts = activePath.split('/')
    parts.pop()
    const folderPrefix = parts.length > 0 ? parts.join('/') + '/' : ''
    
    const folderName = prompt('Enter new folder name:')
    if (folderName) {
      const cleanPath = `${folderPrefix}${folderName.replace(/^\/|\/$/g, '')}/.keep`
      const newId = Date.now().toString()
      setFiles([...files, { id: newId, path: cleanPath, content: '// Empty directory placeholder', language: 'plaintext' }])
      setActiveFileId(newId)
    }
  }

  const handleDeleteFile = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (files.length === 1) return alert('Project must have at least one file.')
    const newFiles = files.filter(f => f.id !== id)
    setFiles(newFiles)
    if (activeFileId === id) setActiveFileId(newFiles[0].id)
  }

  const handleUpdateActiveFile = (key: 'path' | 'content' | 'language', value: string) => {
    setFiles(files.map(f => f.id === activeFileId ? { ...f, [key]: value } : f))
  }

  // --- UPLOAD & DRAG/DROP ENGINE ---

  const getLangFromPath = (path: string) => {
    const ext = path.split('.').pop()?.toLowerCase() || ''
    const map: Record<string, string> = { 
      ts: 'typescript', tsx: 'typescript', js: 'javascript', jsx: 'javascript', 
      py: 'python', json: 'json', md: 'markdown', yml: 'yaml', yaml: 'yaml', 
      html: 'html', css: 'css', sh: 'bash', sql: 'sql', rs: 'rust', go: 'go' 
    }
    return map[ext] || 'plaintext'
  }

  // Recursive directory reader for folder drag & drop
  const processDroppedItems = async (items: DataTransferItemList, targetPathPrefix: string = '') => {
    const newFiles: any[] = []

    const readEntry = async (entry: any, currentPath: string) => {
      if (entry.isFile) {
        const file = await new Promise<File>((resolve) => entry.file(resolve))
        const content = await file.text()
        newFiles.push({
          id: Date.now().toString() + Math.random().toString(),
          path: currentPath + file.name,
          content,
          language: getLangFromPath(file.name)
        })
      } else if (entry.isDirectory) {
        const dirReader = entry.createReader()
        const entries = await new Promise<any[]>((resolve) => dirReader.readEntries(resolve))
        
        // If the folder is empty, create a .keep file to preserve the directory
        if (entries.length === 0) {
          newFiles.push({
            id: Date.now().toString() + Math.random().toString(),
            path: `${currentPath}${entry.name}/.keep`,
            content: '// Empty directory placeholder',
            language: 'plaintext'
          })
        }
        
        for (const subEntry of entries) {
          await readEntry(subEntry, `${currentPath}${entry.name}/`)
        }
      }
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry()
        if (entry) await readEntry(entry, targetPathPrefix)
      }
    }
    return newFiles
  }

  const handleDrop = async (e: React.DragEvent, targetDir: string = '') => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverTarget(null)
    setIsDraggingGlobal(false)

    if (e.dataTransfer.items) {
      const newFiles = await processDroppedItems(e.dataTransfer.items, targetDir)
      if (newFiles.length > 0) {
        // Replace default placeholder if untouched, otherwise append
        if (files.length === 1 && files[0].content === '// Entry point\n') {
          setFiles(newFiles)
        } else {
          setFiles(prev => [...prev, ...newFiles])
        }
        setActiveFileId(newFiles[0].id)
      }
    }
  }

  // Fallback for clicking the upload buttons
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = Array.from(e.target.files || [])
    if (!uploadedFiles.length) return

    const newFiles = await Promise.all(uploadedFiles.map(async (file) => {
      const content = await file.text()
      const path = file.webkitRelativePath || file.name 
      return { id: Date.now().toString() + Math.random().toString(), path, content, language: getLangFromPath(path) }
    }))

    if (files.length === 1 && files[0].content === '// Entry point\n') {
      setFiles(newFiles)
    } else {
      setFiles(prev => [...prev, ...newFiles])
    }
    setActiveFileId(newFiles[0].id)
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (folderInputRef.current) folderInputRef.current.value = ''
  }

  // --- SUBMISSION ---

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const res = await fetch('/api/vault/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          title, description, language, itemType, 
          code: itemType === 'SNIPPET' ? code : undefined,
          files: itemType === 'PROJECT' ? files : undefined
        }),
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || data.message || 'Failed to publish')
      
      router.push('/vault')
      router.refresh()
    } catch (err: any) {
      setError(err.message)
      setLoading(false)
    }
  }

  return (
    <main className="min-h-screen bg-[#020408] text-slate-100 p-4 md:p-8 selection:bg-indigo-500/30">
      <div className="max-w-6xl mx-auto space-y-6">
        
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <Link href="/vault" className="text-sm font-mono text-indigo-400 hover:text-indigo-300 transition-colors flex items-center gap-2 mb-2">
              &larr; cd ../vault
            </Link>
            <h1 className="text-2xl font-black text-white tracking-tight">Deploy to Vault</h1>
          </div>

          <div className="flex bg-[#070b14] p-1.5 rounded-lg border border-slate-800 shadow-inner">
            <button type="button" onClick={() => setItemType('SNIPPET')} className={`px-4 py-2 rounded-md text-xs font-mono font-bold transition-all ${itemType === 'SNIPPET' ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(99,102,241,0.4)]' : 'text-slate-400 hover:text-white'}`}>
              📄 Snippet
            </button>
            <button type="button" onClick={() => setItemType('PROJECT')} className={`px-4 py-2 rounded-md text-xs font-mono font-bold transition-all ${itemType === 'PROJECT' ? 'bg-emerald-600 text-white shadow-[0_0_15px_rgba(16,185,129,0.4)]' : 'text-slate-400 hover:text-white'}`}>
              📁 Project
            </button>
          </div>
        </div>

        {error && <div className="bg-rose-950/40 border border-rose-900 text-rose-400 p-4 rounded-lg font-mono text-xs shadow-[0_0_15px_rgba(225,29,72,0.2)]">{error}</div>}

        <form onSubmit={handleSubmit} className="bg-gradient-to-br from-[#070b14] to-[#020408] border border-slate-800 rounded-2xl p-6 md:p-8 shadow-2xl space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-[10px] font-mono font-bold uppercase tracking-widest text-slate-500 mb-2">Payload Title</label>
              <input type="text" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Secure Auth Boilerplate" className="w-full px-4 py-3 bg-[#020408] border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all shadow-inner" />
            </div>
            <div>
              <label className="block text-[10px] font-mono font-bold uppercase tracking-widest text-slate-500 mb-2">Primary Language</label>
              <input type="text" required value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="e.g., typescript, python, docker" className="w-full px-4 py-3 bg-[#020408] border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all shadow-inner" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono font-bold uppercase tracking-widest text-slate-500 mb-2">Description</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief summary of the architecture" className="w-full px-4 py-3 bg-[#020408] border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all shadow-inner" />
          </div>

          {itemType === 'SNIPPET' ? (
            <div className="w-full h-[500px] border border-slate-800 rounded-xl overflow-hidden shadow-2xl">
              <Editor height="100%" theme="vs-dark" language={language} value={code} onChange={(v) => setCode(v || '')} />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-0 border border-slate-800 rounded-xl overflow-hidden shadow-2xl h-[600px] bg-[#020408]">
              
              {/* Left Pane: File Explorer with Drag & Drop */}
              <div 
                onDragOver={(e) => { e.preventDefault(); setIsDraggingGlobal(true) }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDraggingGlobal(false)
                }}
                onDrop={(e) => handleDrop(e, '')} // Global drop targets root
                className={`md:col-span-4 lg:col-span-3 border-b md:border-b-0 md:border-r flex flex-col transition-colors relative ${
                  isDraggingGlobal ? 'bg-emerald-950/20 border-emerald-500/50' : 'bg-[#050505] border-slate-800'
                }`}
              >
                {/* Global Drop Overlay */}
                {isDraggingGlobal && (
                  <div className="absolute inset-0 z-50 border-2 border-dashed border-emerald-500/50 bg-emerald-950/30 flex items-center justify-center rounded-l-xl backdrop-blur-sm pointer-events-none">
                    <span className="text-emerald-400 font-bold font-mono text-xs uppercase tracking-widest bg-emerald-950 px-4 py-2 rounded-lg border border-emerald-500/30">Drop to Upload</span>
                  </div>
                )}

                <div className="p-3 border-b border-slate-800 bg-[#0a0a0a] flex items-center justify-between z-10 relative">
                  <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Workspace</span>
                  
                  {/* Action Icons */}
                  <div className="flex items-center gap-1.5">
                    <button type="button" onClick={handleAddFile} title="New File" className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20 p-1 bg-emerald-500/10 rounded transition-colors">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    </button>
                    <button type="button" onClick={handleAddFolder} title="New Folder" className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/20 p-1 bg-amber-500/10 rounded transition-colors">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>
                    </button>
                    <div className="w-px h-4 bg-slate-700 mx-1"></div>
                    
                    <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileUpload} multiple />
                    {/* @ts-ignore - React typings for webkitdirectory */}
                    <input type="file" ref={folderInputRef} className="hidden" onChange={handleFileUpload} webkitdirectory="true" directory="true" multiple />
                    
                    <button type="button" onClick={() => fileInputRef.current?.click()} title="Upload Files" className="text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/20 p-1 bg-indigo-500/10 rounded transition-colors">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                    </button>
                    <button type="button" onClick={() => folderInputRef.current?.click()} title="Upload Folder" className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/20 p-1 bg-purple-500/10 rounded transition-colors">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" /></svg>
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-2 space-y-0.5 z-10 relative">
                  {sortedFiles.map(f => {
                    const parts = f.path.split('/')
                    const filename = parts.pop()
                    const folder = parts.length > 0 ? parts.join('/') + '/' : ''
                    const isKeepFile = filename === '.keep'
                    
                    const isDropTarget = dragOverTarget === folder

                    return (
                      <div 
                        key={f.id} 
                        onClick={() => setActiveFileId(f.id)}
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverTarget(folder) }}
                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverTarget(null) }}
                        onDrop={(e) => handleDrop(e, folder)}
                        className={`flex items-center justify-between p-1.5 px-2 rounded-md cursor-pointer text-xs font-mono transition-all group ${
                          isDropTarget ? 'bg-emerald-500/20 border border-emerald-500/50 scale-105 shadow-lg' :
                          activeFileId === f.id ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30' : 
                          'text-slate-400 hover:bg-slate-900 border border-transparent'
                        }`}
                      >
                        <div className="truncate flex-1 flex items-center pointer-events-none">
                          <span className="opacity-40 mr-1.5 text-[10px]">
                            {isKeepFile ? '📁' : '📄'}
                          </span>
                          <span className="truncate">
                            <span className="text-slate-500">{folder}</span>
                            <span className={isKeepFile ? 'text-slate-600 italic' : 'text-slate-200'}>{filename}</span>
                          </span>
                        </div>
                        <button type="button" onClick={(e) => handleDeleteFile(f.id, e)} className="opacity-0 group-hover:opacity-100 text-rose-500 hover:text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 rounded p-0.5 ml-2 transition-all">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Right Pane: Code Editor */}
              <div className="md:col-span-8 lg:col-span-9 flex flex-col bg-[#0a0a0a]">
                {activeFile && (
                  <>
                    <div className="flex flex-wrap items-center gap-3 p-3 border-b border-slate-800 bg-[#050505]">
                      <input 
                        type="text" 
                        value={activeFile.path} 
                        onChange={(e) => handleUpdateActiveFile('path', e.target.value)} 
                        placeholder="Path (e.g. src/app/page.tsx)" 
                        className="flex-1 min-w-[200px] bg-[#020408] border border-slate-800 rounded px-3 py-1.5 text-xs font-mono text-white focus:outline-none focus:border-indigo-500 transition-colors shadow-inner" 
                      />
                      <input 
                        type="text" 
                        value={activeFile.language} 
                        onChange={(e) => handleUpdateActiveFile('language', e.target.value)} 
                        placeholder="Language" 
                        className="w-28 bg-[#020408] border border-slate-800 rounded px-3 py-1.5 text-[10px] font-mono text-slate-400 uppercase focus:outline-none focus:border-indigo-500 transition-colors shadow-inner" 
                      />
                    </div>
                    <div className="flex-1 bg-[#000205]">
                      <Editor 
                        height="100%" 
                        theme="vs-dark" 
                        language={activeFile.language} 
                        value={activeFile.content} 
                        onChange={(v) => handleUpdateActiveFile('content', v || '')} 
                        options={{ minimap: { enabled: false }, fontSize: 13, padding: { top: 16 } }}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-slate-800/80">
            <button type="submit" disabled={loading || !title.trim()} className="bg-gradient-to-r from-emerald-600 to-indigo-600 hover:from-emerald-500 hover:to-indigo-500 text-white font-black font-mono text-xs uppercase tracking-widest px-8 py-4 rounded-xl transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)] disabled:opacity-50">
              {loading ? 'Deploying...' : 'Deploy Payload to Vault'}
            </button>
          </div>
        </form>
      </div>
    </main>
  )
}